import { Component, Inject, OnInit } from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { Case } from '../../../../../../../../_base-shared/models/Case/Case';
import { FormBuilder, FormGroup, UntypedFormArray, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import {
  MAT_DIALOG_DATA,
  MatDialogRef
} from '@angular/material/dialog';
import { PaymentService } from '../../../../payment/payment.service';

@Component({
  standalone: false,
  selector: 'app-rebuild-payment-plan-v2',
  templateUrl: './rebuild-payment-plan-v2.component.html',
  styles: [],
  animations: [
    trigger(
      'inOutAnimation',
      [
        transition(
          ':enter',
          [
            style({height: 0, opacity: 0}),
            animate('0.3s ease-out',
              style({height: '*', opacity: 1}))
          ]
        ),
        transition(
          ':leave',
          [
            animate('0.3s ease-in',
              style({height: 0, opacity: 0}))
          ]
        )
      ]
    )
  ]
})
export class RebuildPaymentPlanV2Component implements OnInit {
  public case: Case;
  public form: FormGroup;
  public isSubmitting: boolean;
  public phaseOneAmount = 0;
  public phaseOneAmountLeft = 0;
  public phaseTwoAmount = 0;
  public phaseTwoAmountLeft = 0;

  constructor(private fb: FormBuilder,
              private translate: TranslateService,
              private toastr: ToastrService,
              private dialogRef: MatDialogRef<RebuildPaymentPlanV2Component>,
              private paymentService: PaymentService,
              @Inject(MAT_DIALOG_DATA) public data: any) {
    this.case = data.case;
  }

  ngOnInit(): void {
    this.calculatePhaseAmount(true);
    this.buildForm();
  }

  public cancelRebuild(): void {
    this.dialogRef.close({dismissed: true});
  }

  public submitForm(form: FormGroup): void {
    if (form.invalid) {
      form.markAllAsTouched();
      return;
    }

    this.isSubmitting = true;
    if (this.case.product.group_slug === 'ds' || this.case.product.group_slug === 'refinance') {
      this.paymentService.rebuildInstallmentPlanV2(this.case.id, form.value)
        .pipe(finalize(() => this.isSubmitting = false))
        .subscribe(
          result => {
            this.toastr.success(this.translate.instant('CASES.single.rebuild-plan-note-success'));
            this.dialogRef.close({dismissed: false, data: result.data, result: true});
          },
          error => this.toastr.error(error.message, this.translate.instant('SHARED.error')),
        );
    }
  }

  private buildForm(): void {
    this.form = this.fb.group({
      rebuild_type: ['full_from_start', [Validators.required]],
      phase_one_installments: this.fb.array([]),
      phase_two_installments: this.fb.array([])
    });


    this.form.controls.phase_one_installments.valueChanges.subscribe(value => {
      this.updateAmountLeft('1', value);
    });
    this.form.controls.phase_two_installments.valueChanges.subscribe(value => {
      this.updateAmountLeft('2', value);
    });
  }

  public getFormArray(phase: string): UntypedFormArray {
    return this.form.get(phase) as UntypedFormArray;
  }

  public addInstallment(phase: string, $event): void {
    $event.preventDefault();

    if (phase === '1') {
      const formArray = this.form.get('phase_one_installments') as UntypedFormArray;
      formArray.push(
        this.fb.group({
          amount: [null]
        })
      );
    }
    if (phase === '2') {
      const formArray = this.form.get('phase_two_installments') as UntypedFormArray;
      formArray.push(
        this.fb.group({
          amount: [null]
        })
      );
    }
  }

  public removeInstallment(phase: string, index: number, $event): void {
    $event.preventDefault();
    const formArray = this.getFormArray(phase);
    formArray.removeAt(index);
  }

  public updateAmountLeft(phase: string, value): void {
    let newValue = 0;

    if (phase === '1') {
      const formArray = this.form.get('phase_one_installments') as UntypedFormArray;
      formArray.value.map(val => {
        newValue += val.amount;
      });
      this.phaseOneAmountLeft = this.phaseOneAmount - newValue;
    }
    if (phase === '2') {
      const formArray = this.form.get('phase_two_installments') as UntypedFormArray;
      formArray.value.map(val => {
        newValue += val.amount;
      });
      this.phaseTwoAmountLeft = this.phaseTwoAmount - newValue;
    }
  }


  public calculatePhaseAmount(fullRebuild: boolean): void {
    if (!fullRebuild) {
      this.case.terms.map(term => {
        if (term.type === 'phase_one') {
          if (term.amount > term.amount_paid) {
            this.phaseOneAmount += term.amount - term.amount_paid;
          }
        }
        if (term.type === 'phase_two') {
          if (term.amount > term.amount_paid) {
            this.phaseTwoAmount += term.amount - term.amount_paid;
          }
        }
      });
    } else {
      this.phaseOneAmount = this.case.debt_payment_plan.phase_one_value || 0;
      this.phaseTwoAmount = this.case.debt_payment_plan.phase_two_value || 0;
    }

    this.phaseOneAmountLeft = this.phaseOneAmount;
    this.phaseTwoAmountLeft = this.phaseTwoAmount;
  }
}
